import React from 'react'
import { observer } from 'mobx-react-lite'
import { endOfMonth } from 'date-fns/endOfMonth'
import { endOfToday } from 'date-fns/endOfToday'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'

import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'

import {
  ANALYTICS_EVENTS,
  Drawer,
  Loader,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useTransactionStore,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { type WidgetProps } from '../core'

import { RecurringSettings } from './components/shared/RecurringSettings'
import { RecurringTransactions } from './components/RecurringTransactions'
import { SkeletonLoader } from '../common/components/SkeletonLoader'

export const RecurringTransactionsWidget = observer(
  ({ onBackClick, onMenuClick, sx = {} }: WidgetProps) => {
    const { recurring: copy } = useGlobalCopyStore()
    const { isInitialized, selectedAccountGuids } = useGlobalUiStore()
    const { alert, isDataLoaded, loadRepeatingTransactionData, setAlert, setDateRange } =
      useRecurringTransactionsStore()
    const { setFilter } = useTransactionStore()
    const { onEvent } = useEvent()
    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false)

    useWidgetLoadTimer({
      widgetName: 'RecurringTransactionsWidget',
      isLoaded: isDataLoaded,
    })

    React.useEffect(() => {
      if (!isDataLoaded) {
        // Load the repeating transactions and related data
        loadRepeatingTransactionData().finally()
      }
    }, [])

    React.useEffect(() => {
      // Sets the Transaction filter
      setFilter({
        dateRange: { start: subDays(startOfToday(), 90), end: endOfToday() },
        accounts: selectedAccountGuids,
      })
      // Set the Date Range for Recurring Transactions
      setDateRange({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) })
    }, [selectedAccountGuids])

    if (!isInitialized) return <Loader />

    return (
      <WidgetContainer
        actions={[
          {
            label: copy.action_title,
            iconName: 'credit_card_gear',
            onClick: () => {
              setIsSettingsOpen(true)
              onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_SETTINGS)
            },
          },
        ]}
        hasDivider={true}
        onAccountsFilterClick={() => onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_FILTER)}
        onBackClick={onBackClick}
        onMenuClick={onMenuClick}
        sx={sx}
        title={copy.title}
      >
        {!isDataLoaded ? (
          <SkeletonLoader sx={{ m: 48 }} variant="list" />
        ) : (
          <RecurringTransactions />
        )}
        <Drawer
          ariaLabelClose={copy.close_drawer_aria}
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          shouldShowHeaderBorders={false}
          title={copy.action_title}
        >
          <RecurringSettings />
        </Drawer>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          autoHideDuration={3500}
          onClose={(_event, reason) => {
            if (reason === 'clickaway') return
            setAlert('', alert.severity)
          }}
          open={Boolean(alert.message)}
        >
          <Alert
            onClose={() => setAlert('', alert.severity)}
            severity={alert.severity}
            variant="filled"
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </WidgetContainer>
    )
  },
)
